import { z } from "zod";

/**
 * LTYPE (linetype) full-fidelity schema — PR-LT1.
 *
 * Source of truth: AutoCAD DXF reference (LTYPE table) + ezdxf reference
 * implementation + plan §10.2. This module depends ONLY on `zod` (no
 * `model.ts` import) to avoid a schema import cycle — `model.ts` imports
 * FROM here, never the reverse.
 *
 * DXF LTYPE group codes (verbatim):
 *   2   name
 *   3   description
 *   70  flags          (16 xrefDep, 32 xrefResolved, 64 referenced)
 *   72  alignment      (always 65 = 'A')
 *   73  element count
 *   40  total pattern length (= Σ|per-element length|)
 *   49  per-element length: >0 dash, <0 gap, =0 dot
 * Complex (per-element):
 *   74  element type   (bit1 abs-rotation, bit2 text, bit4 shape)
 *   75  shape number
 *   340 STYLE handle   (text style / shape-file style)
 *   46  scale          (S=)
 *   50  rotation       (RADIANS — convert from .lin degrees on import)
 *   44  x offset       (X=)
 *   45  y offset       (Y=)
 *   9   text string
 */

/**
 * .lin `R=` (relative) / `A=` (absolute) / `U=` (upright) → code 74 bit1 +
 * render intent for the embedded glyph's rotation reference frame.
 */
const RotationMode = z.enum(["relative", "absolute", "upright"]);

/**
 * Fields shared by complex `text` and `shape` elements (the glyph-host part
 * of the pattern, usually a gap that the glyph is drawn inside).
 */
const ComplexCommon = {
  length: z.number(), // code 49 (signed; glyph host, usually a gap)
  scale: z.number().positive().default(1), // code 46 / S=
  // FIX-3 (Codex#2 PR-LT2 P2 — F12 no-throw): the typed `scale` is `.positive()`,
  // but a dirty/vendor COMPLEX LTYPE element may carry a NON-POSITIVE group 46
  // (0 or negative). Assigning it verbatim to `scale` would make
  // `drawingDocumentSchema.parse` REJECT an otherwise-loadable file (same class
  // as the negative-group-40 FIX-1). Keep the raw 46 in the UNCONSTRAINED
  // `scaleRaw` (export re-emits it byte-exact) and store a SAFE positive `scale`
  // (`|raw| || 1`, AutoCAD's default complex-element scale being 1.0).
  scaleRaw: z.number().optional(), // raw code 46 when non-positive (rare dirty file)
  rotation: z.number().default(0), // code 50, RADIANS (convert from .lin degrees on import)
  rotationMode: RotationMode.default("relative"),
  offsetX: z.number().default(0), // code 44 / X=
  offsetY: z.number().default(0), // code 45 / Y=
  // FIX (Codex PR-LT2 P2 — OMITTED transform-group round-trip): a COMPLEX
  // text/shape element MAY omit the optional transform groups 46 (scale) / 50
  // (rotation) / 44 (x-offset) / 45 (y-offset). Import defaults the omitted ones
  // to the AutoCAD canonical 1 / 0 / 0 / 0, but a `source:"file"` element that
  // genuinely lacked them must NOT gain new groups on export (round-trip adds
  // bytes otherwise). Mirror the `hadTypeFlag` (FIX-6) presence-preservation
  // pattern: import sets these only when the source carried the group; export
  // OMITS the group for a `source:"file"` element that lacked it, and emits it
  // (canonical) for seed / programmatic elements. Per AutoCAD, group 46/50/44/45
  // are independent presence flags, so each is tracked separately.
  had46: z.boolean().optional(), // group 46 present on the source element
  had50: z.boolean().optional(), // group 50 present on the source element
  had44: z.boolean().optional(), // group 44 present on the source element
  had45: z.boolean().optional(), // group 45 present on the source element
};

/**
 * Codex#1 F11 — complex text/shape elements REQUIRE a STYLE reference. Keep
 * BOTH the resolved name (stable across STYLE reindexing) and the raw handle
 * (preserve unresolved dirty handles for round-trip). At least one is
 * expected; the cross-object refcheck is a SEPARATE pass (§10.5, PR-LT2) —
 * NOT enforced here, so a dirty file with neither still parses.
 */
const StyleRef = {
  styleHandle: z.string().optional(),
  styleName: z.string().optional(),
};

/**
 * A single dash/gap/dot or complex (text/shape) pattern element. The `kind`
 * discriminant maps the signed code-49 length to a dash (>0), gap (<0,
 * magnitude stored), or dot (==0); complex elements (74 bit2 text / bit4
 * shape) additionally carry a STYLE reference + transform.
 */
// FIX-6 (Codex#2 P2) — some older/simple DXF writers OMIT group 74 after the
// group-49 length for a plain dash/gap/dot. To round-trip byte-identically the
// SIMPLE element variants carry an optional `hadTypeFlag`: `true` when the
// source element had a 74 (re-emit `74/0`), absent/false when it had none (omit
// 74 on export). Complex (text/shape) elements ALWAYS require a 74, so they
// carry no such flag. Per AutoCAD, 74 for a simple element is always 0, so only
// presence (not value) needs preserving here.
const SimpleTypeFlag = { hadTypeFlag: z.boolean().optional() };

const LinetypeElement = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("dash"), length: z.number().positive(), ...SimpleTypeFlag }), // 49 > 0
  z.object({ kind: z.literal("gap"), length: z.number().positive(), ...SimpleTypeFlag }), // 49 < 0 (store magnitude, sign on serialize)
  z.object({ kind: z.literal("dot"), length: z.literal(0).default(0), ...SimpleTypeFlag }), // 49 == 0
  z.object({ kind: z.literal("text"), text: z.string(), ...StyleRef, ...ComplexCommon }), // 74 bit2
  z.object({
    kind: z.literal("shape"),
    shapeNumber: z.number().int(), // code 75
    ...StyleRef,
    shapeFileName: z.string().optional(), // code 340 STYLE → .shx file name
    shxResolved: z.boolean().default(false), // F11 — missing-SHX dependency state for render
    ...ComplexCommon,
  }), // 74 bit4
]);

/**
 * Typed LTYPE record. NAME-keyed in `document.linetypes`; lookups are
 * case-insensitive (F1/F12).
 *
 * Codex#1 F12 — NO throwing `superRefine`. A reserved-name-with-elements
 * record, or a 40/73 mismatch, is a LOADABLE-but-dirty file; rejecting it at
 * parse time would break rule #1 round-trip (same constraint as design D1).
 * The LTYPE internal-consistency invariants (reserved name ⇒ 0 elements; 73 =
 * element count; 40 = Σ|element length|) are a SEPARATE report-only audit
 * (`auditLinetypeFidelity`) shipped later in PR-LT2, NOT a parse-time reject.
 */
export const linetypeSchema = z.object({
  name: z.string().min(1), // code 2
  description: z.string().default(""), // code 3
  flags: z.number().int().default(0), // code 70 (16 xrefDep, 32 xrefResolved, 64 referenced)
  alignment: z.literal(65).default(65), // code 72 — always 'A'
  // FIX-4 (Codex#2 PR-LT2 P2) — the AutoCAD spec says group 72 is ALWAYS 65
  // ('A'); the typed `alignment` is therefore `z.literal(65)`. A dirty/vendor
  // file may carry a non-65 value, which the literal would normalize to 65 and
  // lose on round-trip. Preserve the file's original 72 here (only set when it
  // differs from 65) so the writer can re-emit `alignmentRaw ?? 65` byte-exact.
  alignmentRaw: z.number().optional(), // raw code 72 when ≠ 65 (rare dirty file)
  patternLength: z.number().nonnegative(), // code 40 — store RAW from file (see round-trip note)
  patternLengthRaw: z.number().optional(), // Codex#2 P2-8 — preserve the file's original 40 even if it ≠ Σ|len|
  // Codex#2 P2 — preserve the file's original group 73 (element count) even
  // when it ≠ `elements.length` (a loadable-but-dirty file may disagree).
  // Analogous to `patternLengthRaw` for group 40. On a plain round-trip the
  // PR-LT2 serializer re-emits THIS raw 73; it only normalizes 73 to
  // `elements.length` on an edit/heal pass (mirrors the F12 round-trip
  // preservation rule — no silent rewrite of an unmodified entry).
  elementCountRaw: z.number().int().optional(),
  elements: z.array(LinetypeElement).default([]),
  // FIX-4 (Codex#2 PR-LT2 P2) — provenance marker distinguishing a linetype
  // that came from the FILE's LTYPE table (`"file"`) from one that is part of
  // the synthetic acad.lin/ISO seed merged in for the refcheck (`"seed"`). The
  // seed must STAY in `document.linetypes` so the linetype refcheck resolves
  // standard names (PR-LT1) — but a plain round-trip must NOT serialize the
  // ~38 unused seed entries that were never in the file. `exportLinetypes`
  // skips `source:"seed"` entries unless they are reserved or actually
  // referenced by the drawing. Absent = treated as `"file"` (conservative:
  // legacy / hand-built docs always emit, never silently dropped).
  source: z.enum(["seed", "file"]).optional(),
  handle: z.string().optional(), // code 5
  ownerHandle: z.string().optional(), // code 330
  // FIX-B (Codex#2 PR-LT2 P2) — whether the source LTYPE record carried the
  // `100/AcDbLinetypeTableRecord` subclass marker. R12 / minimal DXFs omit it;
  // the dxf-io writer re-emits `100` only when this is `true` (or `undefined`
  // for a freshly built / seeded entry — AutoCAD-R13+ default). A parsed R12
  // record stamps `false` so a plain round-trip stays byte-exact (no synthesised
  // `100`). Mirrors the `hadTypeFlag` (FIX-6) presence-preservation pattern.
  hadSubclassMarker: z.boolean().optional(),
  // Unrecognised / vendor-specific group codes captured at parse time
  // (`DxfLinetypeEntry.extraGroups`), preserved verbatim so an import→export
  // round-trip re-emits them (Codex#2 P2 — vendor group round-trip). Mirrors
  // the `{ code, value }` raw-group shape used throughout dxf-io.
  extraGroups: z
    .array(z.object({ code: z.number(), value: z.string() }))
    .optional(),
});

export type Linetype = z.infer<typeof linetypeSchema>;
export type LinetypeInput = z.input<typeof linetypeSchema>;
export type LinetypeElement = z.infer<typeof LinetypeElement>;
